import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SecuritySettings {
  auth_security: {
    recaptcha_enabled: boolean;
    google_oauth_enabled: boolean;
    apple_oauth_enabled: boolean;
    password_min_length: number;
    /** Délai minimum, en jours, entre deux changements d'e-mail. */
    email_change_min_days?: number;
    /** Délai minimum, en jours, entre deux changements de nom d'utilisateur. */
    username_change_min_days?: number;
    /** Pseudos interdits aux membres (un admin peut les prendre). */
    reserved_usernames?: string[];
    /** Longueurs autorisées d'un nouveau pseudo (défauts 3 et 24). */
    username_min_length?: number;
    username_max_length?: number;
    /** Taille maximale (Mo) de la photo choisie avant recadrage (défaut 5). */
    avatar_max_file_size_mb?: number;


    require_email_verification: boolean;
    recaptcha_site_key: string;
    leaked_password_protection_enabled: boolean;
    // Réglages de filtres vidéo (uniquement pour les utilisateurs connectés)
    filter_service_type?: string;
    beauty_filters_enabled?: boolean;
    available_presets?: string[] | null;
    max_skin_smooth_intensity?: number | null;
    max_eye_size_intensity?: number | null;
    max_face_slim_intensity?: number | null;
    allow_custom_filters?: boolean;
    // Zoom maximal de l'enregistreur vidéo (défaut 5x)
    max_zoom_level?: number;
  };
  form_security: {
    recaptcha_on_contact: boolean;
    recaptcha_on_payment: boolean;
    recaptcha_on_sensitive_actions: boolean;
    input_validation_strict: boolean;
    xss_protection: boolean;
    csrf_protection: boolean;
  };
  rate_limiting: {
    login_max_attempts_per_hour: number;
    signup_max_attempts_per_hour: number;
    password_reset_max_per_day: number;
    api_calls_per_minute: number;
    video_upload_max_per_hour?: number;
    product_creation_max_per_day?: number;
    comment_post_max_per_hour?: number;
    max_file_size_mb?: number;
    video_title_max_length?: number;
    video_description_max_length?: number;
    video_tags_min?: number;
    video_tags_max?: number;
  };
  content_moderation?: {
    auto_content_moderation_enabled: boolean;
    strict_mode: boolean;
    /** Motifs proposés dans la fenêtre de signalement (administrables). */
    report_reasons?: { value: string; label: string }[];
  };

}

/**
 * Charge uniquement les réglages NON sensibles exposés par la fonction
 * `get_public_security_settings`. La table `security_settings` contient des
 * secrets et n'est lisible que par les administrateurs.
 */
export const useSecuritySettings = () => {
  return useQuery({
    queryKey: ["public-security-settings"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_security_settings" as any);

      if (error) throw error;

      return (data ?? {}) as unknown as SecuritySettings;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};
